import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItemText,
    Divider,
    Typography,
    IconButton,
    Tooltip,
    Input,
    Collapse,
    CircularProgress,
    ListItem,
} from '@material-ui/core';
import {
    Add,
    KeyboardArrowDown,
    Search,
    KeyboardArrowUp,
} from '@material-ui/icons';
import ImmutableTimer from 'api/immutables/ImmutableTimer';
import { getTimerDurationTxt } from 'util/timer';
import AutoCompleteField from 'components/AutoCompleteField';
import { parseCode } from 'util/utils';
import { Client, Matter } from '../../api/types/types';
import { useContext } from 'react';
import { RootStoreContext } from '../../App';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import * as Styled from './styled';
import { getMatterTooltipText } from 'util/timeEntry';
import { MatterItemFormatter } from 'components/TemplateForm/TemplateForm';

const TransferTimerDialog = () => {
    const rootStore = useContext(RootStoreContext);
    const { t } = useTranslation(['timers', 'common']);
    const { transferTimerDialogStore: store, newTimerDialogStore } = rootStore;
    const {
        isOpen,
        targetTimerId,
        sourceTimerId,
        transferChunks,
        filteredTimers,
        saving,
        setTargetTimerId,
        searchText,
        setSearchText,
        setClientFilter,
        setMatterFilter,
        clientFilter,
        matterFilter,
        closeTransferDialog,
        expanded,
        handleEventType,
        setExpanded
    } = store;
    const filteredTimersWithoutSource = filteredTimers.filter(
        (timer: ImmutableTimer) => timer.id !== sourceTimerId
    );
    const matterLabel = rootStore.appStore.features.EpochConfigMatterLabel;
    const includeSubmitted = rootStore.appStore.features.EpochConfigTimeSegmentsSubmittedVisible;

    const handleTimerSelect = (timerId: number) => {
        setTargetTimerId(timerId);
    };

    const handleTransfer = async (e: React.MouseEvent) => {
        handleEventType(e);
        await transferChunks();
    };

    const handleAddTimer = () => {
        setTargetTimerId();
        newTimerDialogStore.open(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={closeTransferDialog}
            maxWidth="sm"
            fullWidth={true}
        >
            <Styled.DialogHeader>
                {saving && (
                    <Styled.LoadingOverlay>
                        <CircularProgress size={50} color="primary" />
                    </Styled.LoadingOverlay>
                )}
                <Styled.TitleSection>
                    <Typography variant="h6">
                        {t('dialog.transfer_chunk.title')}
                    </Typography>
                    <Tooltip title={t('dialog.transfer_chunk.action.new_timer.tooltip')}>
                        <IconButton
                            onClick={() => {
                                rootStore.appStore.setPreviousActiveElement();
                                handleAddTimer();
                            }}
                        >
                            <Add />
                        </IconButton>
                    </Tooltip>
                </Styled.TitleSection>
                <Styled.SearchSection>
                    <Search style={{ marginRight: '8px' }} />
                    <Input
                        placeholder={t('dialog.transfer_chunk.field.search.placeholder')}
                        fullWidth={true}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ padding: '5px', fontSize: '14px' }}
                    />
                    <Tooltip title={t(`${expanded ? 'collapse' : 'expand'}`, { ns: 'common' })}>
                        <IconButton onClick={setExpanded}>
                            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                    </Tooltip>
                </Styled.SearchSection>
                <Collapse unmountOnExit={true} in={expanded}>
                    <Styled.FiltersContainer>
                        {/* Client Filter */}
                        <Styled.FilterWrapper>
                            <AutoCompleteField
                                label={t('dialog.transfer_chunk.filter.client')}
                                fetch={(search) => rootStore.api.Timer.getDistinctClientsFromTimers(search)}
                                currentItem={clientFilter}
                                getItemText={(c: Client) => parseCode(c.name, c.number)}
                                clearable={true}
                                onClear={() => setClientFilter(null)}
                                onSelect={(client: Client) => setClientFilter(client)}
                            />
                        </Styled.FilterWrapper>
                        {/* Matter Filter */}
                        <Styled.FilterWrapper>
                            <AutoCompleteField
                                label={t('dialog.transfer_chunk.filter.matter', { matterLabel })}
                                fetch={(search: string) => rootStore.api.Timer.getDistinctMattersFromTimers(
                                    search,
                                    clientFilter ? clientFilter.id : undefined
                                )}
                                currentItem={matterFilter}
                                getItemText={(m: Matter) => parseCode(m.name, m.number)}
                                formatItem={MatterItemFormatter}
                                clearable={true}
                                onClear={() => setMatterFilter(null)}
                                onSelect={(matter: Matter) => setMatterFilter(matter)}
                                tooltip={getMatterTooltipText}
                            />
                        </Styled.FilterWrapper>
                    </Styled.FiltersContainer>
                </Collapse>
            </Styled.DialogHeader>
            <DialogContent
                style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 16px' }}
            >
                <List>
                    {filteredTimersWithoutSource.map((timer: ImmutableTimer) => (
                        <React.Fragment key={timer.id}>
                            <ListItem
                                button={true}
                                selected={targetTimerId === timer.id}
                                onClick={() => handleTimerSelect(timer.id!)}
                            >
                                <ListItemText
                                    aria-label={t('timer.name', { name: timer.name })}
                                    primary={timer.name}
                                    secondary={getTimerDurationTxt(timer, includeSubmitted)}
                                    primaryTypographyProps={{
                                        style: {
                                            fontWeight: targetTimerId === timer.id ? 'bold' : 'normal'
                                        }
                                    }}
                                />
                            </ListItem>
                            <Divider />
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleTransfer}
                    disabled={!targetTimerId}
                >
                    {t('save', { ns: 'common' })}
                </Button>
                <Button
                    onClick={(e: React.MouseEvent) => {
                        handleEventType(e);
                        closeTransferDialog();
                    }}
                    color="secondary"
                >
                    {t('cancel', { ns: 'common' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default observer(TransferTimerDialog);
